/**
 * Проценты берутся ТОЛЬКО из position_settings (reward-категории правятся в админке),
 * без захардкоженных дефолтов — иначе цифры из админки не применялись бы для 3/5/7 мест
 * и фронт расходился бы с бэком (бэк собирает таблицу из тех же пресетов).
 */
export function resolveWinPercents(presets) {
    if (!presets?.length) return [];
    return presets.map((p) => Number(p.win) || 0);
}

export function resolveBudgetPercents(presets) {
    if (!presets?.length) return [];
    return presets.map((p) => Number(p.budget_percent) || 0);
}

/**
 * Hamilton / largest-remainder distribution of totalPlaces by preset.win %.
 */
export function distributeWinnersByPercent(presets, totalPlaces) {
    if (!presets?.length || !totalPlaces) {
        return presets?.map(() => 0) || [];
    }

    const winPercents = resolveWinPercents(presets);

    const floorValues = winPercents.map((win, idx) => ({
        originalIndex: idx,
        floorValue: Math.floor((win / 100) * totalPlaces),
        decimalPart: ((win / 100) * totalPlaces) % 1,
    }));

    const sortedByDecimal = [...floorValues].sort((a, b) => b.decimalPart - a.decimalPart);
    const totalFloor = floorValues.reduce((sum, p) => sum + p.floorValue, 0);
    const remaining = totalPlaces - totalFloor;

    const result = floorValues.map((p) => p.floorValue);
    for (let i = 0; i < remaining; i++) {
        result[sortedByDecimal[i].originalIndex] += 1;
    }

    return result;
}

/** Minimum winners per place: at least 1, and at least 1% of total. */
export function getMinWinnersPerPlace(totalPlaces) {
    if (!totalPlaces || totalPlaces <= 0) return 1;
    return Math.max(1, Math.ceil(totalPlaces * 0.01));
}

/**
 * Donor order when editing a place:
 * 1) places below (from last up to edited+1)
 * 2) places above (from edited-1 down to 0)
 *
 * Example (5 places, edit 2nd / index 1): 4 → 3 → 2 → 0
 */
function getRedistributeDonorOrder(n, editedIndex) {
    const order = [];
    for (let i = n - 1; i > editedIndex; i--) order.push(i);
    for (let i = editedIndex - 1; i >= 0; i--) order.push(i);
    return order;
}

/**
 * Shared cascade redistribute for winners / budget.
 * Extra goes to first donor (usually last). Shortage taken from donors in order,
 * each place keeps at least `minPerPlace`.
 */
function redistributePlaceValues(values, editedIndex, desiredRaw, total, minPerPlace, parseValue) {
    const n = values?.length || 0;
    if (n === 0 || !total) return values || [];
    if (n === 1) return [total];

    const next = values.map((c) => Number(c) || 0);
    const maxForEdited = Math.max(minPerPlace, total - minPerPlace * (n - 1));
    const parsed = parseValue(desiredRaw);
    const desired = Number.isNaN(parsed) ? minPerPlace : parsed;
    const clamped = Math.min(Math.max(desired, minPerPlace), maxForEdited);

    const current = next[editedIndex] || 0;
    const delta = clamped - current;
    if (Math.abs(delta) < 1e-12) return next;

    const donors = getRedistributeDonorOrder(n, editedIndex);
    if (!donors.length) return next;

    if (delta < 0) {
        next[editedIndex] = clamped;
        next[donors[0]] = (next[donors[0]] || 0) - delta;
        return next;
    }

    let need = delta;
    next[editedIndex] = clamped;
    for (const i of donors) {
        if (need <= 1e-12) break;
        const available = Math.max(0, (next[i] || 0) - minPerPlace);
        const take = Math.min(available, need);
        next[i] = (next[i] || 0) - take;
        need -= take;
    }

    if (need > 1e-12) {
        next[editedIndex] = clamped - need;
    }

    return next;
}

/**
 * Set place `editedIndex` to `rawValue`.
 * Extra / shortage cascades through donor order, each place keeps min 1%.
 */
export function redistributePlaceWinner(counts, editedIndex, rawValue, totalPlaces) {
    return redistributePlaceValues(
        counts,
        editedIndex,
        rawValue,
        totalPlaces,
        getMinWinnersPerPlace(totalPlaces),
        (raw) => Number.parseInt(String(raw).replace(/[^\d]/g, ''), 10)
    );
}

/**
 * Distribute totalBudget by preset.budget_percent (normalized if sum ≠ 100).
 * Last place absorbs float drift so the sum equals totalBudget.
 */
export function distributeBudgetByPercent(presets, totalBudget) {
    if (!presets?.length || !totalBudget) {
        return presets?.map(() => 0) || [];
    }

    const budgetPercents = resolveBudgetPercents(presets);
    const totalPercent = budgetPercents.reduce((sum, p) => sum + p, 0);
    if (totalPercent === 0) return presets.map(() => 0);

    const amounts = budgetPercents.map((percent) => (percent / totalPercent) * totalBudget);
    if (amounts.length > 1) {
        const headSum = amounts.slice(0, -1).reduce((sum, n) => sum + n, 0);
        amounts[amounts.length - 1] = totalBudget - headSum;
    }
    return amounts;
}

export function getMinBudgetPerPlace(totalBudget) {
    if (!totalBudget || totalBudget <= 0) return 0;
    return totalBudget * 0.01;
}

export function parseBudgetInput(rawValue) {
    const normalized = String(rawValue || '')
        .replace(',', '.')
        .replace(/[^\d.]/g, '');
    if (!normalized) return NaN;
    return Number.parseFloat(normalized);
}

export function formatBudgetAmount(amount) {
    if (amount == null || amount === '') return '';
    const num = Number(amount);
    if (Number.isNaN(num)) return '';
    return num % 1 === 0 ? String(Math.round(num)) : num.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Same cascade redistribution as winners, for budget amounts (supports decimals).
 * Min per place = 1% of totalBudget.
 */
export function redistributePlaceBudget(amounts, editedIndex, rawValue, totalBudget) {
    return redistributePlaceValues(
        amounts,
        editedIndex,
        rawValue,
        totalBudget,
        getMinBudgetPerPlace(totalBudget),
        parseBudgetInput
    );
}

export function isMatchingPlaceTotals(values, total, epsilon = 0.0001) {
    if (!Array.isArray(values) || !total) return false;
    const sum = values.reduce((s, n) => s + (Number(n) || 0), 0);
    return Math.abs(sum - Number(total)) <= epsilon;
}

/**
 * Convert absolute place values into integer percents that always sum to 100
 * (Hamilton / largest-remainder). Does not change the underlying counts.
 */
export function distributePercentsFromCounts(counts) {
    if (!counts?.length) return [];

    const nums = counts.map((c) => Number(c) || 0);
    const total = nums.reduce((sum, n) => sum + n, 0);
    if (total <= 0) return nums.map(() => 0);

    const exact = nums.map((n) => (n / total) * 100);
    const floors = exact.map((v) => Math.floor(v));
    let remaining = 100 - floors.reduce((sum, n) => sum + n, 0);

    const byRemainder = exact
        .map((v, i) => ({ i, rem: v - floors[i] }))
        .sort((a, b) => b.rem - a.rem || a.i - b.i);

    const result = [...floors];
    for (let k = 0; k < remaining; k++) {
        result[byRemainder[k].i] += 1;
    }
    return result;
}
